/**
 * One JSON-LD document per route.
 *
 * Every marketing route describes itself with several schema.org nodes — the
 * shared Organization, WebSite and SoftwareApplication entities plus whatever
 * the page is: WebPage, Article, ItemList, FAQPage, BreadcrumbList. They used to
 * ship as one `<script type="application/ld+json">` block per node, each with
 * its own `@context`, which is valid but leaves nothing to stop two blocks
 * describing the same entity differently, or a second FAQPage landing beside
 * the first when a new schema type is added to a page that already has one.
 * Google's Rich Results Test reports a second FAQPage as a duplicate, and which
 * of two contradictory product nodes it believes is not up to us.
 *
 * So every route's nodes go through `composeRouteGraph()` — called once, from
 * `getSeoForPath()` in `siteSeo.ts` — and are emitted as a single
 * `{ "@context": "https://schema.org", "@graph": [...] }` document by both the
 * prerender head (`entry-server.tsx`) and client-side navigation
 * (`hooks/usePageTitle.ts`). Schema builders keep returning plain arrays of
 * nodes; adding a type to a route means adding a node to that route's array,
 * never a script tag of its own.
 */

export type JsonLd = Record<string, unknown>;

export const SCHEMA_CONTEXT = 'https://schema.org';

/**
 * Marks the route's JSON-LD script. The prerendered page and the client write
 * the same attribute, so a client-side navigation replaces the document rather
 * than adding a second one beside it.
 */
export const ROUTE_SCHEMA_ATTRIBUTE = 'data-route-schema';

/**
 * Types a page carries once. Google reads FAQ rich results from a single FAQPage
 * per page and flags a second as a duplicate field, so two FAQ lists on one
 * route have to become one list — and every question in it has to be visible on
 * the page, which is a decision for whoever adds the second list, not for this
 * function.
 */
const ONE_PER_ROUTE = ['FAQPage'];

const typesOf = (node: JsonLd): string[] => {
  const type = node['@type'];
  if (Array.isArray(type)) return type.filter((value): value is string => typeof value === 'string');
  return typeof type === 'string' ? [type] : [];
};

const sameValue = (a: unknown, b: unknown) => a === b || JSON.stringify(a) === JSON.stringify(b);

/**
 * Normalises a route's nodes into the `@graph` it ships.
 *
 * - The per-node `@context` is dropped; the document declares it once.
 * - Nodes that share an `@id` are one entity, so they become one node in the
 *   position the first occupied. A later node may add properties the entity
 *   does not have yet, and may add a type (`Product` alongside
 *   `SoftwareApplication` is multi-typing, not a conflict). It may not restate a
 *   property with a different value: two descriptions of the same entity on one
 *   page is exactly the contradiction this exists to stop, and the build fails
 *   rather than picking one silently.
 * - A type in `ONE_PER_ROUTE` may appear once.
 *
 * Nodes without an `@id` (the breadcrumb, some FAQ lists) pass through as they
 * are. The input nodes are never mutated — several are shared constants.
 */
export const composeRouteGraph = (route: string, nodes: JsonLd[]): JsonLd[] => {
  const graph: JsonLd[] = [];
  const byId = new Map<string, JsonLd>();

  nodes.forEach((source) => {
    const context = source['@context'];
    if (context !== undefined && context !== SCHEMA_CONTEXT) {
      throw new Error(
        `${route}: a JSON-LD node declares @context ${JSON.stringify(context)}; the route graph only composes ${SCHEMA_CONTEXT} nodes.`,
      );
    }

    const node: JsonLd = { ...source };
    delete node['@context'];

    const id = typeof node['@id'] === 'string' ? node['@id'] : undefined;
    const existing = id ? byId.get(id) : undefined;

    if (!existing) {
      graph.push(node);
      if (id) byId.set(id, node);
      return;
    }

    Object.entries(node).forEach(([key, value]) => {
      if (key === '@type') {
        const types = [...new Set([...typesOf(existing), ...typesOf(node)])];
        if (types.length) existing['@type'] = types.length === 1 ? types[0] : types;
        return;
      }
      if (!(key in existing)) {
        existing[key] = value;
        return;
      }
      if (!sameValue(existing[key], value)) {
        throw new Error(
          `${route}: two JSON-LD nodes describe ${id} with different "${key}" values. State it once, on one node.`,
        );
      }
    });
  });

  ONE_PER_ROUTE.forEach((type) => {
    const count = graph.filter((node) => typesOf(node).includes(type)).length;
    if (count > 1) {
      throw new Error(
        `${route}: composes ${count} ${type} nodes. A page carries one — merge them into the single list the page renders.`,
      );
    }
  });

  return graph;
};

/**
 * The route's composed graph as the text of its one JSON-LD script, or
 * `undefined` when the route carries no structured data. `<` is escaped so no
 * string in the graph can close the script element early.
 */
export const serializeRouteJsonLd = (graph: JsonLd[] | undefined): string | undefined => {
  if (!graph?.length) return undefined;
  return JSON.stringify({ '@context': SCHEMA_CONTEXT, '@graph': graph }).replace(/</g, '\\u003c');
};
